import React from "react";
import TinyEditor from "../Global/TinyEditor";
import { useAppDispatch } from "../../hooks";
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { SubPage } from "../../Models/SubPage";
import { createSubPage } from "../../Services/CommandCenterService";
import { addSubPage } from "../../Features/WikiPages/wikiSlice";

import { msalInstance } from "../../AppAuth";
import { AddIcon } from "@chakra-ui/icons";

interface FormValues {
  label: string;
  content: string;
  createdBy: string;
  wikiPageId: number;
}

const AddSubPageModal = ({ children, wikiPageId }) => {
  const dispatch = useAppDispatch();

  const { isOpen, onOpen, onClose } = useDisclosure({
    onClose: () => {
      //reset the form when the modal is closed
      reset();
    },
  });
  const toast = useToast();
  const activeAccount = msalInstance.getActiveAccount();

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
    control,
  } = useForm<FormValues>();

  const onCreatePage = async ({ label, content, wikiPageId }) => {
    const newPage: SubPage = {
      label: label,
      content: content,
      createdBy: activeAccount?.name || "No Author Found",
      wikiPageId: wikiPageId,
    };

    const result = await createSubPage(newPage);
    const res = await result.json();

    //Update the UI with the new page
    dispatch(addSubPage(res));

    toast({
      title: "Success!",
      description: `The new page was created.`,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    reset();

    onClose();
  };

  return (
    <>
      <Button
        onClick={onOpen}
        size="xs"
        backgroundColor="gray.600"
        color="white"
        fontWeight="medium"
        _hover={{ bg: "gray.700" }}
        _active={{
          bg: "gray.800",
          transform: "scale(0.95)",
        }}
        leftIcon={<AddIcon />}
      >
        {children}
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        blockScrollOnMount={false}
        trapFocus={false}
        returnFocusOnClose={false}
      >
        <ModalOverlay style={{ zIndex: 1280 }} />
        <ModalContent as="form" onSubmit={handleSubmit(onCreatePage)}>
          <ModalHeader fontWeight="bold">Add new page</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={0}>
            <Input
              placeholder=""
              value={wikiPageId}
              id="wikiPageId"
              {...register("wikiPageId", {
                required: "This is required",
                maxLength: { value: 40, message: "Max length should be 40" },
              })}
              hidden={true}
            />
            <FormControl isInvalid={!!errors.label}>
              <FormLabel>Label</FormLabel>
              <Input
                placeholder="Enter label"
                id="label"
                {...register("label", {
                  required: "This is required",
                  maxLength: { value: 40, message: "Max length should be 40" },
                })}
              />
              <FormErrorMessage>
                {errors.label && typeof errors.label.message === "string"
                  ? errors.label.message
                  : undefined}
              </FormErrorMessage>
              {/*<Heading size="sm" mt={4}>
                Select an image
              </Heading>
              <ImageGallery
                onImageChanged={(image) => {
                  setSelectedImageUrl(image);
                }}
              />*/}
              <FormControl mt={4} isInvalid={!!errors.content}></FormControl>
              <FormLabel mt={4}>Image Gallery</FormLabel>
              <Controller
                name="content"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TinyEditor
                    //imageUrl={selectedImageUrl}
                    content={field.value}
                    onContentChange={field.onChange} // onChange updates the form's state
                    editorTitle="Content"
                  />
                )}
              />
              <FormErrorMessage>
                {errors.content && typeof errors.content.message === "string"
                  ? errors.content.message
                  : undefined}
              </FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} mr={3} fontWeight="medium">
              Cancel
            </Button>
            <Button
              isLoading={isSubmitting}
              colorScheme="orange"
              fontWeight="medium"
              type="submit"
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddSubPageModal;
